// Save migrations scaffold (IMPLEMENTATION §2.9 "Load path", M11 fixtures, N4 v1 → v2). The envelope's `v`
// is the save format; `MIGRATIONS[v]` lifts a raw envelope from v to v + 1, and `migrate` chains them up to
// SAVE_VERSION. A save from a NEWER build is refused (never downgraded in place). Every SimState shape change
// from M5 on bumps sim/state.ts STATE_VERSION and adds a migration here. M11: every format has a FROZEN
// fixture (tests/save/fixtures/save-v<n>.json, written once by `node tools/gen-save-fixture.ts`) that must
// keep loading through this table; tests/save/migrations.test.ts also proves the chain on a synthetic format 0.
import { STATE_VERSION } from '../sim/state.ts';

/** Save format version written by this build (the envelope `v`). */
export const SAVE_VERSION = 1;

export interface RawEnvelope {
  v: number;
  sim: { v: number } & Record<string, unknown>;
  [k: string]: unknown;
}

export type Migration = (env: RawEnvelope) => RawEnvelope;

/** v → v + 1. Empty in v0.1 (format 1 is the first). */
export const MIGRATIONS: Readonly<Record<number, Migration>> = {};

export type MigrateResult =
  | { ok: true; env: RawEnvelope; steps: number }
  | { ok: false; reason: string };

export function migrate(
  env: RawEnvelope,
  table: Readonly<Record<number, Migration>> = MIGRATIONS,
  target = SAVE_VERSION,
): MigrateResult {
  if (env.v > target)
    return { ok: false, reason: `save format ${env.v} is newer than this build (${target})` };
  let cur = env;
  let steps = 0;
  while (cur.v < target) {
    const m = table[cur.v];
    if (!m) return { ok: false, reason: `no migration from save format ${cur.v}` };
    const next = m(cur);
    if (next.v !== cur.v + 1)
      return { ok: false, reason: `migration ${cur.v} did not produce ${cur.v + 1}` };
    cur = next;
    steps += 1;
  }
  if (cur.sim.v !== STATE_VERSION)
    return {
      ok: false,
      reason: `sim state v${cur.sim.v} after migration, expected v${STATE_VERSION}`,
    };
  return { ok: true, env: cur, steps };
}
